import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService, UIOrg } from '../service/auth.service';
import { catchError, of } from 'rxjs';
import {
  AuthorizeOauthUserPostRequest,
  GetClientAccount200Response,
  OauthService,
} from 'ldt-identity-service-api';
import { HostnameService } from '../../hostname-router-service';

interface OAuthParams {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  state: string;
  code_challenge?: string;
  code_challenge_method?: string;
}

@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrls: ['./oauth-consent.component.scss'],
  standalone: false,
})
export class OAuthConsentComponent implements OnInit {
  oauthParams: OAuthParams;

  clientDetails: GetClientAccount200Response | null = null;
  scopes: string[] = [];
  loading = true;
  error: string | null = null;
  authorizing = false;
  isWorkforceLogin: boolean = false;
  orgs: UIOrg[] = [];
  selectedOrgId: string | null = null;
  showOrgSelection = false;
  showMcpReconfigureWarning = false;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private oauthService: OauthService
  ) {}

  ngOnInit(): void {
    this.isWorkforceLogin = HostnameService.isWorkforceHostname();
    this.extractQueryParams();
    this.loadUserOrgs();
  }

  private extractQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      this.showMcpReconfigureWarning = false;
      this.error = null;

      // Requests from the deprecated mcp.workforce.ai server include a "metadata" query
      // param; those clients must be reconfigured to mcp.gotlivedata.com before authorizing.
      if (params['metadata'] !== undefined) {
        this.showMcpReconfigureWarning = true;
        this.loading = false;
        return;
      }

      this.oauthParams = {
        client_id: params['client_id'] || '',
        redirect_uri: params['redirect_uri'] || '',
        response_type: params['response_type'] || '',
        scope: params['scope'] || '',
        state: params['state'] || '',
        code_challenge: params['code_challenge'],
        code_challenge_method: params['code_challenge_method'],
      };

      if (!this.validateParams()) {
        this.error =
          'Invalid OAuth parameters. Please try again later or contact support@livedatatechnologies.com for help.';
        this.loading = false;
        return;
      }

      this.scopes = this.oauthParams.scope.split(' ').filter((s) => s);
      this.loadClientDetails();
    });
  }

  private validateParams(): boolean {
    return !!(
      this.oauthParams.client_id &&
      this.oauthParams.redirect_uri &&
      this.oauthParams.response_type &&
      this.oauthParams.state &&
      this.oauthParams.code_challenge &&
      this.oauthParams.code_challenge_method
    );
  }

  private loadClientDetails(): void {
    this.loading = true;

    this.oauthService.getClientAccount(this.oauthParams.client_id).subscribe({
      next: (response) => {
        this.clientDetails = response;
        this.loading = false;
      },
      error: (err) => {
        this.error =
          'Client not found. Please try again later or contact support@livedatatechnologies.com for help.';
        this.loading = false;
      },
    });
  }

  private loadUserOrgs(): void {
    this.authService.$getOrgs.subscribe((orgs) => {
      this.orgs = orgs;
      if (orgs.length === 1) {
        this.selectedOrgId = orgs[0].id;
        this.showOrgSelection = false;
      } else if (orgs.length > 1) {
        this.showOrgSelection = true;
      }
    });
  }

  authorize(): void {
    if (!this.selectedOrgId) {
      this.error = 'Please select an organization to continue.';
      return;
    }

    this.authorizing = true;
    this.error = null;

    const authorizeOauthUserPostRequest: AuthorizeOauthUserPostRequest = {
      client_id: this.oauthParams.client_id,
      redirect_uri: this.oauthParams.redirect_uri,
      response_type: this.oauthParams.response_type,
      scope: this.oauthParams.scope,
      state: this.oauthParams.state,
      code_challenge: this.oauthParams.code_challenge!,
      code_challenge_method: this.oauthParams.code_challenge_method!,
      org_id: this.selectedOrgId,
    };

    this.oauthService
      .authorizeOauthUserPost(authorizeOauthUserPostRequest)
      .pipe(
        catchError((err) => {
          this.error =
            'Authorization failed. Please contact support@livedatatechnologies.com for help.';
          this.authorizing = false;
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response && response.redirect_uri) {
          window.location.href = response.redirect_uri;
        }
      });
  }

  deny(): void {
    const errorParams = new URLSearchParams({
      error: 'access_denied',
      error_description: 'User denied the consent request',
      state: this.oauthParams.state,
    });

    const deniedUrl = `${this.oauthParams.redirect_uri}?${errorParams.toString()}`;
    window.location.href = deniedUrl;
  }

  getScopeDescription(scope: string): string {
    const scopeDescriptions: { [key: string]: string } = {
      read: 'Read your profile information',
      write: 'Update your profile information',
      email: 'Access your email address',
      profile: 'Access your basic profile information',
      openid: 'Authenticate using your identity',
      claudeai: 'Use your account to make API calls while using Claude',
    };

    return scopeDescriptions[scope] || scope;
  }
}
